import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService, DashboardStats } from '../../services/adminService';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalFarmers: 0,
    totalBuyers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    activeOrders: 0,
    pendingProducts: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    const response = await adminService.getDashboardStats();
    if (response.success && response.data) {
      setStats(response.data);
    }
    setLoading(false);
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: '👥',
      color: 'bg-blue-500',
      link: '/admin/users',
    },
    {
      title: 'Farmers',
      value: stats.totalFarmers,
      icon: '🌾',
      color: 'bg-green-500',
      link: '/admin/users?role=farmer',
    },
    {
      title: 'Buyers',
      value: stats.totalBuyers,
      icon: '🛒',
      color: 'bg-purple-500',
      link: '/admin/users?role=buyer',
    },
    {
      title: 'Products',
      value: stats.totalProducts,
      icon: '📦',
      color: 'bg-orange-500',
      link: '/admin/products',
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: '📋',
      color: 'bg-indigo-500',
      link: '/admin/orders',
    },
    {
      title: 'Revenue',
      value: `KSh ${stats.totalRevenue.toLocaleString()}`,
      icon: '💰',
      color: 'bg-green-600',
      link: '/admin/analytics',
    },
    {
      title: 'Active Orders',
      value: stats.activeOrders,
      icon: '🚀',
      color: 'bg-yellow-500',
      link: '/admin/orders?status=active',
    },
    {
      title: 'Pending Products',
      value: stats.pendingProducts,
      icon: '⏳',
      color: 'bg-red-500',
      link: '/admin/products?status=pending',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-8 text-white">
        <h2 className="text-3xl font-bold mb-2">Welcome to Admin Dashboard</h2>
        <p className="text-green-100">
          Manage your AgriMarket platform, monitor activities, and control operations.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <Link
            key={index}
            to={card.link}
            className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 border border-gray-100"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900">{card.value}</p>
              </div>
              <div className={`${card.color} w-14 h-14 rounded-xl flex items-center justify-center text-3xl`}>
                {card.icon}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/users/new"
            className="flex items-center space-x-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all"
          >
            <span className="text-2xl">➕</span>
            <span className="font-medium text-blue-900">Add User</span>
          </Link>
          <Link
            to="/admin/products?status=pending"
            className="flex items-center space-x-3 px-4 py-3 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition-all"
          >
            <span className="text-2xl">⏳</span>
            <span className="font-medium text-yellow-900">Review Products</span>
          </Link>
          <Link
            to="/admin/orders"
            className="flex items-center space-x-3 px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg transition-all"
          >
            <span className="text-2xl">📋</span>
            <span className="font-medium text-green-900">View Orders</span>
          </Link>
          <Link
            to="/admin/analytics"
            className="flex items-center space-x-3 px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg transition-all"
          >
            <span className="text-2xl">📈</span>
            <span className="font-medium text-purple-900">Analytics</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">👤</span>
              <div className="flex-1">
                <p className="text-sm text-gray-900">New user registered</p>
                <p className="text-xs text-gray-500">2 minutes ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-2xl">📦</span>
              <div className="flex-1">
                <p className="text-sm text-gray-900">Product submitted for review</p>
                <p className="text-xs text-gray-500">15 minutes ago</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <span className="text-2xl">💰</span>
              <div className="flex-1">
                <p className="text-sm text-gray-900">New order placed - KSh 2,500</p>
                <p className="text-xs text-gray-500">1 hour ago</p>
              </div>
            </div>
          </div>
        </div>

        {/* System Alerts */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">System Alerts</h3>
          <div className="space-y-3">
            {stats.pendingProducts > 0 && (
              <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                <span className="text-xl">⚠️</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">
                    {stats.pendingProducts} products awaiting approval
                  </p>
                  <Link to="/admin/products?status=pending" className="text-xs text-yellow-700 underline">
                    Review now
                  </Link>
                </div>
              </div>
            )}
            {stats.activeOrders > 10 && (
              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-xl">📦</span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-900">
                    {stats.activeOrders} active orders in progress
                  </p>
                  <Link to="/admin/orders" className="text-xs text-blue-700 underline">
                    View all
                  </Link>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
              <span className="text-xl">✅</span>
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">
                  All systems operational
                </p>
                <p className="text-xs text-green-700">Last checked: Just now</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

